import { Button } from '@heroui/react';
import { CaseUpper, Heading, Trash2, Type } from 'lucide-react';
import React, { FC } from 'react';

import { useAdGeneratorSettings, useUpdateAdGeneratorSettings } from '../../../../../queries';
import { createTextElement, TextPreset } from '../../../model/factories';
import { useAdsGeneratorStore } from '../../../model/store';
import { PanelSection } from '../../common';
import { EmptyPanelHint, OptionCard, OptionGrid, SavedRow } from '../Editor.styled';

const PRESETS: { preset: TextPreset; label: string; icon: typeof Type }[] = [
  { preset: 'headline', label: 'Nagłówek', icon: Heading },
  { preset: 'subtitle', label: 'Podtytuł', icon: Type },
  { preset: 'body', label: 'Akapit', icon: Type },
  { preset: 'badge', label: 'Plakietka CTA', icon: CaseUpper },
];

export const TextPanel: FC = () => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const addElement = useAdsGeneratorStore((state) => state.addElement);
  const { fonts, textStyles } = useAdGeneratorSettings();
  const { mutate: saveSettings } = useUpdateAdGeneratorSettings();

  if (!scene) return null;

  return (
    <PanelSection title="Tekst">
      <OptionGrid>
        {PRESETS.map(({ preset, label, icon: Icon }) => (
          <OptionCard
            key={preset}
            type="button"
            onClick={() => addElement(createTextElement(preset, scene.width, scene.height, fonts[preset]))}
          >
            <Icon size={18} />
            {label}
          </OptionCard>
        ))}
      </OptionGrid>
      <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Zapisane style</p>
        {textStyles.length === 0 ? (
          <EmptyPanelHint>
            Ustaw napis po swojemu i zapisz go zakładką w panelu po prawej, żeby wracał tu jednym kliknięciem.
          </EmptyPanelHint>
        ) : textStyles.map((style) => (
          <SavedRow key={style.id}>
            <OptionCard
              type="button"
              className="flex-1"
              onClick={() => addElement({
                ...createTextElement('subtitle', scene.width, scene.height, style.fontFamily),
                text: style.name,
                fontSize: Math.max(8, Math.round(Math.min(scene.width, scene.height) * style.sizeRatio)),
                fontWeight: style.fontWeight,
                color: style.color,
                uppercase: style.uppercase,
                letterSpacing: style.letterSpacing,
                lineHeight: style.lineHeight,
                name: style.name,
              })}
            >
              {style.name}
            </OptionCard>
            <Button
              size="sm"
              radius="sm"
              isIconOnly
              variant="light"
              color="danger"
              aria-label="Usuń styl"
              onPress={() => saveSettings({ textStyles: textStyles.filter((item) => item.id !== style.id) })}
            >
              <Trash2 size={14} />
            </Button>
          </SavedRow>
        ))}
      </div>

      <p className="text-xs text-gray-500 leading-relaxed">
        Treść zmienisz podwójnym kliknięciem na planszy. Krój domyślny dla każdego rodzaju tekstu ustawisz
        w ustawieniach generatora.
      </p>
    </PanelSection>
  );
};
